import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { environment } from "@env";
import {
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  SessionInfo,
  UpdateProfileRequest,
  UserProfile,
} from "@shared/models/auth.models";
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";

import { TokenService } from "./token.service";

interface UserEnvelope {
  user: UserProfile;
}

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly apiUrl = environment.apiBaseUrl;
  private currentUser$ = new BehaviorSubject<UserProfile | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshRequest$: Observable<LoginResponse> | null = null;

  readonly user$ = this.currentUser$.asObservable();

  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private router = inject(Router);

  get currentUser(): UserProfile | null {
    return this.currentUser$.value;
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login-password`, credentials)
      .pipe(
        tap((response) => {
          this.tokenService.setTokens(response.accessToken);
          this.currentUser$.next(response.user);
          this.scheduleRefresh();
        }),
      );
  }

  logout(): void {
    this.http
      .post(`${this.apiUrl}/auth/logout`, {})
      .pipe(catchError(() => of(null)))
      .subscribe();
    this.clearSession();
    this.router.navigate(["/login"]);
  }

  refreshToken(): Observable<LoginResponse> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshRequest$ = this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/refresh`, {})
      .pipe(
        tap((response) => {
          this.tokenService.setTokens(response.accessToken);
          if (response.user) {
            this.currentUser$.next(response.user);
          }
          this.scheduleRefresh();
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  getSession(): Observable<SessionInfo> {
    return this.http
      .get<SessionInfo>(`${this.apiUrl}/auth/session`)
      .pipe(tap((session) => this.currentUser$.next(session.user)));
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserEnvelope>(`${this.apiUrl}/auth/profile`).pipe(
      map((response) => response.user),
      tap((profile) => this.currentUser$.next(profile)),
    );
  }

  /**
   * Resolves the current user at app startup. If the in-memory access token
   * is missing or expired, falls back to the HTTP-only refresh-token cookie
   * to mint a fresh access token before fetching the profile. Returns `null`
   * when no valid session can be established.
   */
  hydrateCurrentUser(): Observable<UserProfile | null> {
    const token = this.tokenService.getAccessToken();
    if (!token || this.tokenService.isTokenExpired()) {
      this.tokenService.clearTokens();
      return this.refreshToken().pipe(
        switchMap(() => this.getProfile()),
        catchError(() => {
          this.currentUser$.next(null);
          this.tokenService.clearTokens();
          return of(null);
        }),
      );
    }

    if (this.currentUser$.value) {
      return of(this.currentUser$.value);
    }

    return this.getProfile().pipe(
      catchError(() => {
        this.currentUser$.next(null);
        return of(null);
      }),
    );
  }

  updateProfile(data: UpdateProfileRequest): Observable<UserProfile> {
    return this.http
      .patch<UserEnvelope>(`${this.apiUrl}/auth/profile`, data)
      .pipe(
        map((response) => response.user),
        tap((profile) => this.currentUser$.next(profile)),
      );
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/password/forgot`, data);
  }

  resetPassword(data: ResetPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/password/reset`, data);
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiresInMs = this.tokenService.getTokenExpiryMs();
    if (!expiresInMs || expiresInMs <= 0) return;

    const refreshAtMs = Math.floor(expiresInMs * 0.73);
    this.refreshTimer = setTimeout(() => {
      this.refreshToken()
        .pipe(
          catchError(() => {
            this.clearSession();
            this.router.navigate(["/login"]);
            return of(null);
          }),
        )
        .subscribe();
    }, refreshAtMs);
  }

  private clearSession(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.tokenService.clearTokens();
    this.currentUser$.next(null);
  }
}
